import { socialSecurityEstimate } from "./social-security.js";
import { taxLiability, type FilingStatus } from "./tax-liability.js";

/**
 * Turns a household's real numbers into the inputs a retirement
 * simulation needs. The simulations answer "will OUR plan work?", so
 * they have to start from what the household will actually have, not
 * from a FIRE number derived from spending alone.
 *
 * All amounts are integer cents in today's dollars (real terms).
 *
 * **Web mirrors this** at web/src/lib/util/retirement-plan.ts.
 */

/** Money set aside for someone or something else. It counts toward net
 *  worth but can't fund your retirement. */
const EARMARKED_ACCOUNT_TYPES: ReadonlySet<string> = new Set([
  "529",
  "529_prepaid",
  "utma",
  "daf",
]);
const PRE_TAX_ACCOUNT_TYPES: ReadonlySet<string> = new Set([
  "401k_traditional",
  "ira_traditional",
  "ira_sep",
]);
const TAX_FREE_ACCOUNT_TYPES: ReadonlySet<string> = new Set([
  "401k_roth",
  "ira_roth",
  "hsa",
]);

/** Plan through this age, so a long life doesn't count as a failure. */
export const PLAN_END_AGE = 95;
/** Never plan for less than this, whatever the retirement age. */
export const MIN_PLAN_YEARS = 30;
/** Medicare starts at 65; health insurance is on you before that. */
export const MEDICARE_AGE = 65;
export const DEFAULT_SOCIAL_SECURITY_CLAIM_AGE = 67;
/** Share of a taxable-account sale assumed to be long-term gain. */
const TAXABLE_GAIN_SHARE = 0.5;
/** Brackets are today's dollars, matching the simulation's real terms. */
const PLAN_TAX_YEAR = 2026;

interface SnapshotLike {
  readonly net_worth_cents: number;
  readonly by_account_type?: Readonly<Record<string, number>>;
}

/** Net worth minus money earmarked for others (529, UTMA, DAF). */
export function fiAssetsCents(snapshot: SnapshotLike): number {
  let earmarked = 0;
  for (const [type, value] of Object.entries(snapshot.by_account_type ?? {})) {
    if (EARMARKED_ACCOUNT_TYPES.has(type)) earmarked += value;
  }
  return snapshot.net_worth_cents - earmarked;
}

export interface AccountTaxMix {
  readonly pre_tax_share: number;
  readonly tax_free_share: number;
  readonly taxable_share: number;
}

/** How FI assets split by tax treatment. Shares sum to 1, or 0 if empty. */
export function accountTaxMix(
  byAccountType: Readonly<Record<string, number>> | undefined,
): AccountTaxMix {
  let preTax = 0;
  let taxFree = 0;
  let taxable = 0;
  for (const [type, value] of Object.entries(byAccountType ?? {})) {
    if (value <= 0 || EARMARKED_ACCOUNT_TYPES.has(type)) continue;
    if (PRE_TAX_ACCOUNT_TYPES.has(type)) preTax += value;
    else if (TAX_FREE_ACCOUNT_TYPES.has(type)) taxFree += value;
    else taxable += value;
  }
  const total = preTax + taxFree + taxable;
  if (total === 0) {
    return { pre_tax_share: 0, tax_free_share: 0, taxable_share: 0 };
  }
  return {
    pre_tax_share: preTax / total,
    tax_free_share: taxFree / total,
    taxable_share: taxable / total,
  };
}

export interface RetirementTaxInput {
  /** What you want to spend after tax. */
  readonly annual_spend_cents: number;
  readonly mix: AccountTaxMix;
  readonly filing_status?: FilingStatus;
  readonly include_ny?: boolean;
  readonly include_nyc?: boolean;
}

/**
 * Tax as a share of the gross withdrawal needed to net the spend,
 * assuming you draw from each account type in proportion to its size.
 * Solves by iteration because tax depends on the withdrawal, which
 * depends on the tax.
 */
export function estimateRetirementTaxRatePct(input: RetirementTaxInput): number {
  const { annual_spend_cents: spend, mix } = input;
  if (spend <= 0) return 0;
  let withdrawal = spend;
  let tax = 0;
  for (let i = 0; i < 12; i++) {
    tax = taxLiability({
      ordinary_income_cents: Math.round(withdrawal * mix.pre_tax_share),
      ltcg_cents: Math.round(
        withdrawal * mix.taxable_share * TAXABLE_GAIN_SHARE,
      ),
      qdivs_cents: 0,
      filing_status: input.filing_status ?? "mfj",
      tax_year: PLAN_TAX_YEAR,
      include_ny: input.include_ny ?? true,
      include_nyc: input.include_nyc ?? true,
    }).total_cents;
    withdrawal = spend + tax;
  }
  return (tax / withdrawal) * 100;
}

/** What you'd have to withdraw to net `spend` at this tax rate. */
export function grossUpForTax(spendCents: number, taxRatePct: number): number {
  return Math.round(spendCents / (1 - taxRatePct / 100));
}

export interface ProjectPortfolioInput {
  readonly current_cents: number;
  readonly monthly_savings_cents: number;
  readonly annual_return_rate_pct: number;
  readonly years: number;
}

/** Grow a balance with monthly compounding and contributions. */
export function projectPortfolioCents(input: ProjectPortfolioInput): number {
  const monthlyRate = input.annual_return_rate_pct / 100 / 12;
  let balance = input.current_cents;
  for (let m = 0; m < input.years * 12; m++) {
    balance = balance * (1 + monthlyRate) + input.monthly_savings_cents;
  }
  return Math.max(0, Math.round(balance));
}

export interface RetirementPlanInput {
  readonly fi_assets_cents: number;
  readonly monthly_net_savings_cents: number;
  readonly annual_return_rate_pct: number;
  readonly current_year: number;
  readonly retirement_target_age: number;
  readonly birth_year_primary?: number | undefined;
  readonly birth_year_spouse?: number | undefined;
  readonly social_security_pia_cents?: number | undefined;
  readonly social_security_claim_age?: number | undefined;
  readonly social_security_pia_spouse_cents?: number | undefined;
  readonly social_security_claim_age_spouse?: number | undefined;
  readonly pre_medicare_healthcare_annual_cents?: number | undefined;
  /** Stop saving and retire this year instead of at the target age. */
  readonly retire_today?: boolean;
}

export interface RetirementPlanProjection {
  /** False when no birth year is set, so the plan can't look ahead. */
  readonly has_ages: boolean;
  readonly years_to_retirement: number;
  readonly retirement_year: number;
  readonly age_at_retirement: number;
  readonly projected_portfolio_cents: number;
  readonly horizon_years: number;
  /** Per retirement year, added to spending. Positive for extra costs
   *  like healthcare, negative for income like Social Security. */
  readonly annual_adjustments_cents: readonly number[];
}

export function buildRetirementPlan(input: RetirementPlanInput): RetirementPlanProjection {
  const birthYear = input.birth_year_primary;
  const hasAges = birthYear !== undefined;
  const currentAge = hasAges ? input.current_year - birthYear : null;

  const yearsToRetirement =
    input.retire_today === true || currentAge === null
      ? 0
      : Math.max(0, input.retirement_target_age - currentAge);
  const retirementYear = input.current_year + yearsToRetirement;
  const ageAtRetirement =
    currentAge === null
      ? input.retirement_target_age
      : currentAge + yearsToRetirement;

  const spouseAgeAtRetirement =
    input.birth_year_spouse === undefined
      ? null
      : retirementYear - input.birth_year_spouse;
  const youngestAge =
    spouseAgeAtRetirement === null
      ? ageAtRetirement
      : Math.min(ageAtRetirement, spouseAgeAtRetirement);
  const horizonYears = Math.max(MIN_PLAN_YEARS, PLAN_END_AGE - youngestAge);

  const primaryBenefit = annualBenefit(
    input.social_security_pia_cents,
    input.social_security_claim_age,
  );
  const spouseBenefit = annualBenefit(
    input.social_security_pia_spouse_cents,
    input.social_security_claim_age_spouse,
  );
  const healthcare = input.pre_medicare_healthcare_annual_cents ?? 0;

  const adjustments: number[] = [];
  for (let y = 0; y < horizonYears; y++) {
    const age = ageAtRetirement + y;
    const spouseAge =
      spouseAgeAtRetirement === null ? null : spouseAgeAtRetirement + y;
    let adjustment = 0;
    if (age < MEDICARE_AGE || (spouseAge !== null && spouseAge < MEDICARE_AGE)) {
      adjustment += healthcare;
    }
    if (primaryBenefit && age >= primaryBenefit.claim_age) {
      adjustment -= primaryBenefit.annual_cents;
    }
    if (
      spouseBenefit &&
      spouseAge !== null &&
      spouseAge >= spouseBenefit.claim_age
    ) {
      adjustment -= spouseBenefit.annual_cents;
    }
    adjustments.push(adjustment);
  }

  return {
    has_ages: hasAges,
    years_to_retirement: yearsToRetirement,
    retirement_year: retirementYear,
    age_at_retirement: ageAtRetirement,
    projected_portfolio_cents: projectPortfolioCents({
      current_cents: input.fi_assets_cents,
      monthly_savings_cents: input.monthly_net_savings_cents,
      annual_return_rate_pct: input.annual_return_rate_pct,
      years: yearsToRetirement,
    }),
    horizon_years: horizonYears,
    annual_adjustments_cents: adjustments,
  };
}

function annualBenefit(
  piaCents: number | undefined,
  claimAge: number | undefined,
): { annual_cents: number; claim_age: number } | null {
  if (piaCents === undefined || piaCents <= 0) return null;
  const claim_age = claimAge ?? DEFAULT_SOCIAL_SECURITY_CLAIM_AGE;
  return {
    annual_cents: socialSecurityEstimate({
      pia_at_fra_cents: piaCents,
      claim_age,
    }),
    claim_age,
  };
}
